import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, MapPin } from "lucide-react";
import { IMAGES, LOGO_EXPRESSO } from "../../lib/site";
import { MaskedLine } from "../Reveal";

export default function Hero() {
  const { scrollY } = useScroll();
  const y = useTransform(scrollY, [0, 800], [0, 220]);
  const scale = useTransform(scrollY, [0, 800], [1.05, 1.18]);

  return (
    <section data-testid="hero" className="relative min-h-screen bg-navy overflow-hidden flex flex-col">
      <motion.div style={{ y, scale }} className="absolute inset-0">
        <img
          src={IMAGES.hero}
          alt="Les coureurs du Club Cycliste Le Boulou à l'entraînement"
          className="w-full h-full object-cover"
        />
      </motion.div>
      <div className="absolute inset-0 bg-navy/55" />
      <div className="absolute inset-0 bg-gradient-to-t from-navy via-transparent to-navy/60" />

      <div className="relative flex-1 flex flex-col justify-end max-w-[1400px] w-full mx-auto px-5 sm:px-8 pb-16 pt-40">
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.2, duration: 0.7 }}
          className="flex items-center gap-3 mb-6"
        >
          <span className="h-px w-14 bg-rouge" />
          <span className="flex items-center gap-2 text-white/80 text-xs sm:text-sm font-bold uppercase tracking-[0.3em]">
            <MapPin size={14} className="text-rouge" />
            Le Boulou · Pyrénées-Orientales
          </span>
        </motion.div>

        <h1 className="font-heading uppercase font-semibold tracking-tighter leading-[0.88] text-white text-[15vw] sm:text-[11vw] lg:text-[9rem]">
          <MaskedLine delay={0.35}>Club Cycliste</MaskedLine>
          <MaskedLine delay={0.5}>
            <span>
              Le Boulou<span className="text-rouge">.</span>
            </span>
          </MaskedLine>
        </h1>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8, duration: 0.7 }}
          data-testid="hero-expresso"
          className="mt-5 flex items-center gap-4 sm:gap-5"
        >
          <img
            src={LOGO_EXPRESSO}
            alt="Expresso Racing — logo"
            data-testid="hero-expresso-logo"
            className="h-12 w-12 sm:h-16 sm:w-16 lg:h-20 lg:w-20 rounded-full ring-2 ring-white/25"
          />
          <p className="font-heading uppercase font-semibold tracking-[0.18em] text-rouge text-3xl sm:text-5xl lg:text-6xl">
            Expresso Racing
          </p>
        </motion.div>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9, duration: 0.7 }}
          className="mt-4 max-w-xl text-white/70 text-base sm:text-lg"
        >
          Compétition, formation, loisir et passion du vélo au cœur des Pyrénées-Orientales.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.05, duration: 0.7 }}
          className="mt-10 flex flex-wrap gap-4"
        >
          <Link
            to="/le-club"
            data-testid="hero-cta-club"
            className="group inline-flex items-center gap-3 bg-rouge text-white font-heading uppercase tracking-wider text-sm px-8 py-4 hover:bg-white hover:text-navy transition-[background-color,color] duration-300"
          >
            Découvrir le club
            <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1.5" />
          </Link>
          <Link
            to="/equipes"
            data-testid="hero-cta-equipes"
            className="group inline-flex items-center gap-3 border border-white/40 text-white font-heading uppercase tracking-wider text-sm px-8 py-4 hover:border-rouge hover:text-rouge transition-[border-color,color] duration-300"
          >
            Nos équipes
            <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1.5" />
          </Link>
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4 }}
        className="absolute bottom-8 right-6 sm:right-10 hidden sm:flex flex-col items-center gap-3"
      >
        <span className="text-white/50 text-[10px] uppercase tracking-[0.3em] [writing-mode:vertical-lr]">Défiler</span>
        <span className="relative h-16 w-px bg-white/20 overflow-hidden">
          <span className="absolute inset-x-0 h-1/2 bg-rouge animate-scroll-cue" />
        </span>
      </motion.div>
    </section>
  );
}
